import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'
import rehypeSlug from 'rehype-slug'
import type { Components } from 'react-markdown'
import {
  fetchDocMarkdown,
  fetchWikiPage,
  fetchWikiPageByPath,
  saveWikiPageContent,
  patchWikiPage,
} from '../api/client'
import { useAuth } from '../context/AuthContext'
import { encodeDocPath, resolveMdHref } from '../lib/paths'
import Breadcrumbs from '../components/Breadcrumbs'
import CopyLinkButton from '../components/CopyLinkButton'
import WikiEditor, { type WikiEditorHandle } from '../components/WikiEditor'
import MediaPicker from '../components/MediaPicker'
import RevisionHistory from '../components/RevisionHistory'
import type { WikiPageDetail } from '../types/wiki'
import BananaSlug from 'github-slugger'
import { Pencil, Save, X, History, ImagePlus, Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react'
import 'highlight.js/styles/github-dark.css'

const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: ['className'],
    pre: ['className'],
    span: ['className'],
    h1: ['id', 'className'],
    h2: ['id', 'className'],
    h3: ['id', 'className'],
    h4: ['id', 'className'],
    h5: ['id', 'className'],
    h6: ['id', 'className'],
  },
}

type EditMode = 'visual' | 'markdown'

/**
 * Collect unique corpus-relative link targets referenced by markdown so they
 * can be resolved to wiki page slugs once (before render) rather than per-link.
 */
function extractRelativeTargets(md: string, currentPath: string): string[] {
  const targets = new Set<string>()
  const re = /!?\[[^\]]*\]\(([^)]+)\)/g
  let m: RegExpExecArray | null
  while ((m = re.exec(md)) !== null) {
    const href = m[1].trim()
    if (!href || href.startsWith('#') || href.startsWith('mailto:') || /^https?:\/\//i.test(href)) continue
    const resolved = resolveMdHref(currentPath, href)
    if (resolved) targets.add(resolved)
  }
  return [...targets]
}

async function resolveLinkTargets(md: string, currentPath: string): Promise<Record<string, string>> {
  const targets = extractRelativeTargets(md, currentPath)
  if (targets.length === 0) return {}
  const map: Record<string, string> = {}
  await Promise.all(
    targets.map(async (path) => {
      try {
        const info = await fetchWikiPageByPath(path)
        if (info.linked && info.readable && info.slug) map[path] = info.slug
      } catch {
        // non-page target (media/asset) — caller falls back to /api/doc
      }
    })
  )
  return map
}

function extractHeadings(md: string): { level: number; text: string; id: string }[] {
  const slugger = new BananaSlug()
  const out: { level: number; text: string; id: string }[] = []
  for (const line of md.split(/\r?\n/)) {
    const m = /^(#{1,6})\s+(.+)$/.exec(line)
    if (!m) continue
    const level = m[1].length
    const text = m[2].trim().replace(/\s+#+\s*$/, '').trim()
    out.push({ level, text, id: slugger.slug(text) })
  }
  return out
}

function canUserEdit(role: string | null, page: WikiPageDetail | null): boolean {
  if (!page) return false
  if (role === 'admin' || role === 'editor') return true
  if (role === 'user' && page.canEdit) return true
  return false
}

function canPublish(role: string | null): boolean {
  return role === 'admin' || role === 'editor'
}

export default function WikiPage() {
  const { slug = '' } = useParams()
  const { role } = useAuth()
  const editorRef = useRef<WikiEditorHandle>(null)
  const [page, setPage] = useState<WikiPageDetail | null>(null)
  const [md, setMd] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [editMode, setEditMode] = useState<EditMode>('visual')
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [mediaOpen, setMediaOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [linkMap, setLinkMap] = useState<Record<string, string>>({})

  const reload = useCallback(async () => {
    const detail = await fetchWikiPage(slug)
    setPage(detail)
    const body = await fetchDocMarkdown(detail.corpus_path)
    setMd(body)
    setDraft(body)
    setLinkMap(await resolveLinkTargets(body, detail.corpus_path))
    return { detail, body }
  }, [slug])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setErr(null)
    setIsEditing(false)
    void (async () => {
      try {
        const { body } = await reload()
        if (cancelled) return
        setDraft(body)
      } catch (e: unknown) {
        if (cancelled) return
        const status =
          e && typeof e === 'object' && 'response' in e
            ? (e as { response?: { status?: number; data?: { error?: string } } }).response?.status
            : undefined
        const msg =
          e && typeof e === 'object' && 'response' in e
            ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
            : undefined
        if (status === 403) setErr('This page is not published.')
        else setErr(msg || 'Failed to load page')
        setPage(null)
        setMd(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [slug, reload])

  useEffect(() => {
    if (!isEditing || md === null) return
    const handler = (e: BeforeUnloadEvent) => {
      if (draft !== md) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isEditing, md, draft])

  const headings = useMemo(() => (md ? extractHeadings(isEditing ? draft : md) : []), [md, draft, isEditing])

  const components = useMemo((): Components => {
    const corpusPath = page?.corpus_path ?? ''
    return {
      a({ href, children }) {
        if (!href) return <span>{children}</span>
        if (/^https?:\/\//i.test(href)) {
          return (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary-400 underline hover:text-primary-300">
              {children}
            </a>
          )
        }
        const resolved = resolveMdHref(corpusPath, href)
        if (resolved) {
          const slug = linkMap[resolved]
          if (slug) {
            return (
              <Link to={'/p/' + slug} className="text-primary-400 underline hover:text-primary-300">
                {children}
              </Link>
            )
          }
          return (
            <a href={`/api/doc/${encodeDocPath(resolved)}`} className="text-primary-400 underline">
              {children}
            </a>
          )
        }
        return (
          <a href={href} className="text-primary-400 underline">
            {children}
          </a>
        )
      },
      img({ src, alt }) {
        if (!src) return null
        if (/^https?:\/\//i.test(src)) {
          return (
            <img src={src} alt={alt ?? ''} loading="lazy" className="max-w-full rounded-lg border border-gray-700 my-4" />
          )
        }
        const resolved = resolveMdHref(corpusPath, src)
        const imgSrc = resolved ? `/api/doc/${encodeDocPath(resolved)}` : src
        return (
          <img src={imgSrc} alt={alt ?? ''} loading="lazy" className="max-w-full rounded-lg border border-gray-700 my-4" />
        )
      },
    }
  }, [page?.corpus_path, linkMap])

  const editable = canUserEdit(role, page)
  const publishable = canPublish(role)

  const handleStartEdit = () => {
    if (md === null) return
    setDraft(md)
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setDraft(md ?? '')
    setIsEditing(false)
  }

  const handleSave = async () => {
    if (!page || md === null) return
    const password = window.prompt('Enter your password to save changes:')
    if (password === null) return
    setSaving(true)
    try {
      await saveWikiPageContent(slug, { markdown: draft, password })
      const body = await fetchDocMarkdown(page.corpus_path)
      setMd(body)
      setDraft(body)
      setIsEditing(false)
      const detail = await fetchWikiPage(slug)
      setPage(detail)
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Save failed'
          : 'Save failed'
      window.alert(msg)
    } finally {
      setSaving(false)
    }
  }

  const handleTogglePublish = async () => {
    if (!page) return
    setPublishing(true)
    try {
      const updated = await patchWikiPage(slug, { published: !page.published })
      setPage(updated)
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Publish toggle failed'
          : 'Publish toggle failed'
      window.alert(msg)
    } finally {
      setPublishing(false)
    }
  }

  const handleMediaSelect = (corpusPath: string) => {
    const snippet = `![](${corpusPath})`
    if (editMode === 'visual') {
      editorRef.current?.insertMarkdown(snippet)
    } else {
      setDraft((prev) => (prev ? `${prev}\n\n${snippet}` : snippet))
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading page…
      </div>
    )
  }

  if (err || !page || md === null) {
    return (
      <div className="flex items-center gap-2 text-error-400 bg-red-950/30 border border-red-900/40 rounded-lg p-4 m-4">
        <AlertCircle className="w-5 h-5 shrink-0" />
        <span>{err || 'Could not load page.'}</span>
      </div>
    )
  }

  const updated = new Date(page.updated_at)
  const updatedLabel = Number.isNaN(updated.getTime()) ? page.updated_at : updated.toLocaleString()

  return (
    <article className="doc-md w-full max-w-[min(100%,72rem)] mx-auto px-4 lg:px-10 py-6 lg:py-8 text-gray-200">
      <Breadcrumbs
        items={[
          { label: 'Home', to: '/' },
          { label: page.shelf.name, to: `/s/${page.shelf.slug}` },
          { label: page.book.name, to: `/b/${page.book.slug}` },
          { label: page.chapter.name },
          { label: page.title },
        ]}
      />

      <div className="no-print flex flex-wrap justify-end gap-2 mb-4">
        {editable && !isEditing && (
          <button
            type="button"
            onClick={handleStartEdit}
            className="inline-flex items-center gap-2 text-sm font-medium rounded-lg px-3 py-2 min-h-[44px] bg-gray-800/90 border border-gray-700 text-gray-200 hover:bg-gray-800"
          >
            <Pencil className="w-4 h-4" />
            Edit
          </button>
        )}
        {editable && isEditing && (
          <>
            <div className="inline-flex rounded-lg border border-gray-700 overflow-hidden">
              <button
                type="button"
                onClick={() => setEditMode('visual')}
                className={`min-h-[44px] px-3 text-sm font-medium ${
                  editMode === 'visual' ? 'bg-primary-900/40 text-primary-100' : 'text-gray-400 hover:bg-gray-800'
                }`}
              >
                Visual
              </button>
              <button
                type="button"
                onClick={() => setEditMode('markdown')}
                className={`min-h-[44px] px-3 text-sm font-medium ${
                  editMode === 'markdown' ? 'bg-primary-900/40 text-primary-100' : 'text-gray-400 hover:bg-gray-800'
                }`}
              >
                Markdown
              </button>
            </div>
            <button
              type="button"
              onClick={() => setMediaOpen(true)}
              className="inline-flex items-center gap-2 text-sm font-medium rounded-lg px-3 py-2 min-h-[44px] bg-gray-800/90 border border-gray-700 text-gray-200 hover:bg-gray-800"
            >
              <ImagePlus className="w-4 h-4" />
              Media
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => void handleSave()}
              className="inline-flex items-center gap-2 text-sm font-medium rounded-lg px-3 py-2 min-h-[44px] bg-primary-900/40 border border-primary-700/50 text-primary-100 hover:bg-primary-900/60 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={handleCancelEdit}
              className="inline-flex items-center gap-2 text-sm font-medium rounded-lg px-3 py-2 min-h-[44px] bg-gray-800/90 border border-gray-700 text-gray-200 hover:bg-gray-800 disabled:opacity-50"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => setHistoryOpen(true)}
          className="inline-flex items-center gap-2 text-sm font-medium rounded-lg px-3 py-2 min-h-[44px] bg-gray-800/90 border border-gray-700 text-gray-200 hover:bg-gray-800"
        >
          <History className="w-4 h-4" />
          History
        </button>
        {publishable && (
          <button
            type="button"
            disabled={publishing}
            onClick={() => void handleTogglePublish()}
            className="inline-flex items-center gap-2 text-sm font-medium rounded-lg px-3 py-2 min-h-[44px] bg-gray-800/90 border border-gray-700 text-gray-200 hover:bg-gray-800 disabled:opacity-50"
          >
            {page.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {publishing ? 'Updating…' : page.published ? 'Unpublish' : 'Publish'}
          </button>
        )}
        <CopyLinkButton />
      </div>

      {!isEditing && headings.length > 0 && (
        <nav className="no-print mb-6 rounded-lg border border-gray-700/60 bg-gray-900/40 p-4 text-sm" aria-label="On this page">
          <p className="text-gray-400 font-medium mb-2">On this page</p>
          <ul className="space-y-1">
            {headings.map((h) => (
              <li key={h.id} style={{ paddingLeft: (h.level - 1) * 12 }}>
                <a href={`#${h.id}`} className="text-primary-400 hover:text-primary-300 underline">
                  {h.text}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {isEditing ? (
        <div className="no-print space-y-2">
          {editMode === 'visual' ? (
            <WikiEditor ref={editorRef} initialMarkdown={draft} onChange={setDraft} />
          ) : (
            <>
              <label htmlFor="wiki-edit-area" className="text-sm text-gray-400">
                Editing markdown (Save requires your password)
              </label>
              <textarea
                id="wiki-edit-area"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="w-full min-h-[24rem] font-mono text-sm bg-gray-900/80 border border-gray-700 rounded-lg p-4 text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                spellCheck={false}
              />
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4 [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:text-gray-100 [&_h1]:pt-4 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-gray-100 [&_h2]:mt-8 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-gray-200 [&_p]:leading-relaxed [&_li]:my-1 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight, rehypeSlug, [rehypeSanitize, sanitizeSchema]]}
            components={components}
          >
            {md}
          </ReactMarkdown>
        </div>
      )}

      <footer className="no-print mt-10 pt-4 border-t border-gray-800 text-xs text-gray-500">
        Last updated: <time dateTime={page.updated_at}>{updatedLabel}</time>
        {!page.published && <span className="ml-2 text-amber-500/90">(draft)</span>}
      </footer>

      <MediaPicker
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        onSelect={handleMediaSelect}
        canUpload={publishable}
      />

      <RevisionHistory
        slug={slug}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        currentMarkdown={md}
        canRestore={editable}
        onRestored={() => void reload()}
      />
    </article>
  )
}
